import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { Volume2, Mic, Square, Check, X, ArrowRight, Star, Keyboard, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, playWord, playWordTwice, playCorrectChime, playWrongBuzz, unlockAudioPlayback, interruptCurrentAudio } from "../lib/api";
import { shuffle } from "../lib/shuffle";
import useSwipe from "../hooks/useSwipe";
import WordImage from "../components/WordImage";
import ImageToggle from "../components/ImageToggle";
import OnScreenKeyboard from "../components/OnScreenKeyboard";
import { Shuffle } from "lucide-react";

const FEEDBACK_DELAY_MS = 1800;

export default function TestMode() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [order, setOrder] = useState([]);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState("type"); // "type" | "speak"
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // { correct, correctWord, userAnswer }
  const [busy, setBusy] = useState(false);
  const [speakingWord, setSpeakingWord] = useState(false);
  const [recording, setRecording] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [score, setScore] = useState({ right: 0, wrong: 0 });
  const [showImage, setShowImage] = useState(false); // off by default in tests (it's a big hint)
  const [chosenCount, setChosenCount] = useState(null); // null = show picker first
  const [missedWords, setMissedWords] = useState(null); // null=loading, [] = none, [...] = list

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wordlists/${id}`);
        setList(data);
      } catch { toast.error("List not found"); navigate("/wordlists"); }
    })();
  }, [id, navigate]);

  // Fetch missed words once the list is loaded, so the picker can show the
  // "Review missed words" button with an accurate count.
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/wordlists/${id}/missed-words`);
        setMissedWords(data.words || []);
      } catch (err) {
        console.warn("[TestMode] missed-words fetch failed:", err);
        setMissedWords([]);
      }
    })();
  }, [id]);

  // Swipe-left to advance. Declared at the top level so it's unconditional.
  // It only fires when we're showing feedback — otherwise it no-ops so the
  // kid doesn't accidentally skip the question.
  const swipe = useSwipe({
    onSwipeLeft: () => {
      if (!feedback) return;
      setFeedback(null);
      if (idx < order.length - 1) setIdx(idx + 1);
      else setIdx(order.length);
    },
  });

  // Start the test once the kid chooses how many words they want
  const startTest = (count) => {
    if (!list) return;
    // iOS: ensure audio is unlocked inside this user-gesture handler so
    // the auto-play of the first word isn't blocked by Safari.
    try { unlockAudioPlayback(); } catch { /* ignore */ }
    const shuffled = shuffle(list.words);
    const n = count === "all" ? shuffled.length : Math.min(count, shuffled.length);
    setChosenCount(n);
    setOrder(shuffled.slice(0, n));
    setIdx(0);
    setScore({ right: 0, wrong: 0 });
    setAnswer(""); setFeedback(null);
  };

  // Start a focused review using only the words the kid has recently missed.
  const startMissedTest = () => {
    if (!missedWords || !missedWords.length) return;
    try { unlockAudioPlayback(); } catch { /* ignore */ }
    const shuffled = shuffle(missedWords);
    setChosenCount(shuffled.length);
    setOrder(shuffled);
    setIdx(0);
    setScore({ right: 0, wrong: 0 });
    setAnswer(""); setFeedback(null);
  };

  const word = order[idx];

  useEffect(() => {
    if (!word) return;
    // Cancel-on-word-change guard: prevents the OLD playWordTwice's
    // secondary-voice playback (~500ms after primary) from cutting into
    // the NEW word's audio when the kid taps a new question fast or when
    // React StrictMode double-mounts the page in dev.
    let cancelled = false;
    setAnswer(""); setFeedback(null);
    // Auto-pronounce twice so the kid has a chance to catch it
    (async () => {
      if (cancelled) return;
      try { setSpeakingWord(true); await playWordTwice(word, 450, { shouldStop: () => cancelled }); }
      catch { /* ignore */ }
      finally {
        if (!cancelled) { setSpeakingWord(false); inputRef.current?.focus?.(); }
      }
    })();
    return () => {
      cancelled = true;
      interruptCurrentAudio();
    };
  }, [word]);

  const pronounce = async () => {
    if (!word) return;
    setSpeakingWord(true);
    try { await playWord(word); } catch { toast.error("Couldn't play audio"); }
    finally { setSpeakingWord(false); }
  };

  const checkAnswer = async (userAnswerRaw) => {
    const userAnswer = (userAnswerRaw || "").trim().toLowerCase().replace(/[^a-z]/g, "");
    const correctWord = word.toLowerCase();
    const correct = userAnswer === correctWord;
    setFeedback({ correct, correctWord, userAnswer });
    setScore((s) => ({ right: s.right + (correct ? 1 : 0), wrong: s.wrong + (correct ? 0 : 1) }));
    // Audio feedback — ding for right, soft buzz for wrong
    try { if (correct) playCorrectChime(); else playWrongBuzz(); } catch { /* ignore */ }
    if (correct) {
      setConfetti(true);
      setTimeout(() => setConfetti(false), 2200);
    }
    try {
      await api.post("/progress/attempt", {
        wordlist_id: list.id, word: correctWord,
        mode: mode === "type" ? "test_type" : "test_speak",
        correct, user_answer: userAnswer,
      });
    } catch { /* ignore */ }
    // Auto-advance after delay — always advance index so the last word
    // transitions us into the finished state (idx >= total).
    setTimeout(() => {
      setFeedback(null);
      setIdx((i) => i + 1);
    }, FEEDBACK_DELAY_MS + (correct ? 600 : 0));
  };

  const onSubmitType = (e) => {
    e?.preventDefault?.();
    if (!answer.trim() || busy || feedback) return;
    setBusy(true);
    checkAnswer(answer).finally(() => setBusy(false));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("file", blob, "spell.webm");
        form.append("target", word); // let server validate against multiple interpretations
        try {
          setBusy(true);
          const { data } = await api.post("/stt", form, { headers: { "Content-Type": "multipart/form-data" } });
          // If the server recognized any valid interpretation (e.g. "D O G Dog"
          // or "See Ay Tee"), treat it as the correct spelling.
          if (data.correct) {
            await checkAnswer(word);
          } else {
            const heard = (data.letters || "").toLowerCase();
            if (!heard) {
              toast.error("Didn't catch that. Try again!");
            } else {
              await checkAnswer(heard);
            }
          }
        } catch (err) {
          if (err?.response?.status === 402) {
            toast.warning("Cloud voice check is out of credits", {
              description: "Switch to Type mode, or top up your Universal Key.",
            });
          } else {
            toast.error("Couldn't hear you — try again");
          }
        }
        finally { setBusy(false); }
      };
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission needed");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  if (!list) return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;

  // Step 1 — kid picks how many words to test on
  if (chosenCount === null) {
    const max = list.words.length;
    const presets = [5, 10, 20, 50].filter((n) => n < max);
    return (
      <div className="space-y-6" data-testid="test-length-picker">
        <div>
          <div className="text-sm font-bold text-[#FF6B6B] uppercase tracking-widest">Test mode</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">{list.name}</h1>
        </div>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.35 }}
          className="card-soft text-center py-10"
        >
          <div className="inline-flex w-16 h-16 rounded-3xl bg-[#FBBF24] items-center justify-center shadow-[0_6px_0_#B45309]">
            <Star className="w-8 h-8 text-[#1E1B4B] fill-[#1E1B4B]" />
          </div>
          <h2 className="font-display text-3xl font-bold text-[#1E1B4B] mt-4">How many words?</h2>
          <p className="text-slate-600 mt-1 font-semibold">Pick the size of today's test</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {presets.map((n) => (
              <button
                key={n}
                onClick={() => startTest(n)}
                data-testid={`pick-count-${n}`}
                className="btn-clay-blue min-w-[110px]"
              >
                {n} words
              </button>
            ))}
            <button
              onClick={() => startTest("all")}
              data-testid="pick-count-all"
              className="btn-clay min-w-[110px]"
            >
              All {max}
            </button>
          </div>

          {missedWords && missedWords.length > 0 && (
            <div className="mt-6 pt-6 border-t-2 border-dashed border-blue-100">
              <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Focused review</p>
              <button
                onClick={startMissedTest}
                data-testid="pick-missed-btn"
                className="btn-clay-yellow mt-2 min-w-[240px]"
              >
                <RefreshCw className="w-5 h-5" strokeWidth={3} />
                Review {missedWords.length} missed word{missedWords.length === 1 ? "" : "s"}
              </button>
              <p className="text-xs text-slate-500 mt-2">
                Only the words you got wrong recently — great for a quick fix-up.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  if (!order.length) return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;

  const total = order.length;
  const progress = total ? ((idx + 1) / total) * 100 : 0;
  const finished = idx >= total;

  const reshuffle = () => {
    if (!chosenCount) return;
    const shuffled = shuffle(list.words);
    const n = Math.min(chosenCount, shuffled.length);
    setOrder(shuffled.slice(0, n));
    setIdx(0);
    setScore({ right: 0, wrong: 0 });
    setAnswer(""); setFeedback(null);
    toast.success("Shuffled!");
  };

  return (
    <div className="space-y-6 relative" data-testid="test-page" {...swipe}>
      {confetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          <Confetti numberOfPieces={200} recycle={false} gravity={0.3} />
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-bold text-[#FF6B6B] uppercase tracking-widest">Test mode</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1E1B4B]">{list.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reshuffle} data-testid="test-shuffle-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-sm bg-white text-[#1E1B4B] border-2 border-blue-200 hover:bg-blue-50 transition-colors" title="Shuffle words">
            <Shuffle className="w-4 h-4" strokeWidth={3} /> Shuffle
          </button>
          <div className="chip bg-green-50 text-green-800" data-testid="score-right">✓ {score.right}</div>
          <div className="chip bg-rose-50 text-rose-800" data-testid="score-wrong">✗ {score.wrong}</div>
          <div className="chip" data-testid="test-progress">{Math.min(idx + 1, total)} / {total}</div>
        </div>
      </div>

      <div className="h-3 w-full rounded-full bg-blue-100 overflow-hidden">
        <motion.div className="h-full bg-[#FF6B6B]" animate={{ width: `${progress}%` }} transition={{ type: "spring", bounce: 0.2 }} />
      </div>

      <div className="flex justify-center gap-2 flex-wrap" data-testid="mode-switcher">
        <button
          onClick={() => setMode("type")}
          className={`px-5 py-2.5 rounded-full font-bold text-sm transition ${mode === "type" ? "bg-[#4D96FF] text-white shadow-[0_3px_0_#1D4ED8]" : "bg-white border-2 border-blue-200 text-[#1E1B4B]"}`}
          data-testid="mode-type-btn"
        >
          <Keyboard className="inline w-4 h-4 mr-1" strokeWidth={3} /> Type
        </button>
        <button
          onClick={() => setMode("speak")}
          className={`px-5 py-2.5 rounded-full font-bold text-sm transition ${mode === "speak" ? "bg-[#FF6B6B] text-white shadow-[0_3px_0_#9F1239]" : "bg-white border-2 border-blue-200 text-[#1E1B4B]"}`}
          data-testid="mode-speak-btn"
        >
          <Mic className="inline w-4 h-4 mr-1" strokeWidth={3} /> Speak
        </button>
        <ImageToggle show={showImage} onToggle={setShowImage} testId="test-image-toggle" />
      </div>

      <AnimatePresence mode="wait">
        {!finished && (
          <motion.div
            key={`${idx}-${mode}`}
            initial={{ y: 30, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -30, opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", bounce: 0.4 }}
            className={`card-soft text-center py-10 ${feedback?.correct === false ? "shake" : ""}`}
            data-testid="test-card"
          >
            <button
              onClick={pronounce}
              disabled={speakingWord}
              className="mx-auto w-24 h-24 rounded-full bg-[#4D96FF] text-white flex items-center justify-center shadow-[0_8px_0_#1D4ED8] active:translate-y-[4px] active:shadow-[0_4px_0_#1D4ED8] transition-all disabled:opacity-80"
              data-testid="big-speaker-btn"
              title="Hear the word"
            >
              <Volume2 className={`w-12 h-12 ${speakingWord ? "pulse-soft" : ""}`} strokeWidth={3} />
            </button>
            <p className="mt-4 font-display text-2xl font-bold text-[#1E1B4B]">Listen carefully and spell the word</p>
            <p className="text-sm text-slate-500">Tap the speaker to hear again</p>

            {showImage && (
              <div className="mt-5 flex flex-col items-center" data-testid="test-image-wrap">
                <WordImage word={word} visible={showImage} size="md" />
                <p className="text-xs text-amber-700 font-bold mt-2">Picture hint is on</p>
              </div>
            )}

            {feedback ? (
              <motion.div
                initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", bounce: 0.5 }}
                className="mt-8"
                data-testid="feedback-panel"
              >
                {feedback.correct ? (
                  <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-100 text-green-800 font-bold text-xl" data-testid="feedback-correct">
                    <Check className="w-6 h-6" strokeWidth={3} /> Correct! You spelled <span className="underline">{feedback.correctWord}</span>
                    <Star className="w-5 h-5 text-[#FBBF24] fill-[#FBBF24]" />
                  </div>
                ) : (
                  <div className="inline-flex flex-col items-center gap-1 px-6 py-3 rounded-2xl bg-rose-100 text-rose-800" data-testid="feedback-wrong">
                    <div className="inline-flex items-center gap-2 font-bold text-xl">
                      <X className="w-6 h-6" strokeWidth={3} /> Almost!
                    </div>
                    <div className="text-sm">You said <b>{feedback.userAnswer || "(nothing)"}</b>. The answer is <b className="underline">{feedback.correctWord}</b>.</div>
                  </div>
                )}
              </motion.div>
            ) : mode === "type" ? (
              <form onSubmit={onSubmitType} className="mt-7 max-w-md mx-auto" data-testid="type-form">
                <input
                  ref={inputRef}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSubmitType(e)}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="type the word"
                  className="input-kid"
                  data-testid="spelling-input"
                />
                <OnScreenKeyboard
                  value={answer}
                  onChange={setAnswer}
                  onEnter={() => onSubmitType()}
                  disabled={busy || !!feedback}
                />
                <button type="submit" disabled={busy || !answer.trim()} className="btn-clay mt-4 w-full" data-testid="submit-answer-btn">
                  Check my answer
                </button>
              </form>
            ) : (
              <div className="mt-7">
                {!recording ? (
                  <button onClick={startRecording} disabled={busy} className="btn-clay" data-testid="start-record-btn">
                    <Mic className="w-5 h-5" strokeWidth={3} />
                    {busy ? "Checking…" : "Tap & say the letters"}
                  </button>
                ) : (
                  <button onClick={stopRecording} className="btn-clay-green pulse-soft" data-testid="stop-record-btn">
                    <Square className="w-5 h-5" strokeWidth={3} /> Stop & check
                  </button>
                )}
                <p className="text-xs text-slate-500 mt-3">Tip: say each letter clearly — like "C, A, T"</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {finished && (() => {
        const pct = total ? Math.round((score.right / total) * 100) : 0;
        let emoji, headline, pep, bg, shadow;
        if (pct === 100) {
          emoji = "🏆"; headline = "Perfect score!"; pep = `Every single word right — you're a spelling champion!`;
          bg = "bg-[#FBBF24]"; shadow = "#B45309";
        } else if (pct >= 80) {
          emoji = "🌟"; headline = "Spelling star!"; pep = `${score.right} right out of ${total} — amazing work!`;
          bg = "bg-[#10B981]"; shadow = "#047857";
        } else if (pct >= 50) {
          emoji = "👏"; headline = "Great job!"; pep = `${score.right} right out of ${total}. You're learning fast!`;
          bg = "bg-[#4D96FF]"; shadow = "#1D4ED8";
        } else {
          emoji = "💪"; headline = "Nice try!"; pep = `You got ${score.right} out of ${total}. Practice a few words and try again!`;
          bg = "bg-[#FF6B6B]"; shadow = "#9F1239";
        }
        return (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", bounce: 0.5 }}
            className="card-soft text-center py-12"
            data-testid="test-complete"
          >
            <div className={`inline-flex w-24 h-24 rounded-3xl ${bg} items-center justify-center text-5xl`} style={{ boxShadow: `0 8px 0 ${shadow}` }}>
              <span aria-hidden="true">{emoji}</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B] mt-5">{headline}</h2>
            <p className="text-slate-600 mt-2 text-lg max-w-md mx-auto" data-testid="pep-text">{pep}</p>

            <div className="mt-7 grid grid-cols-3 gap-3 max-w-md mx-auto">
              <div className="card-soft !p-4 !border-green-100 bg-green-50" data-testid="final-right">
                <div className="text-3xl font-display font-bold text-green-700">{score.right}</div>
                <div className="text-xs font-bold uppercase text-green-700/80">Correct</div>
              </div>
              <div className="card-soft !p-4 !border-rose-100 bg-rose-50" data-testid="final-wrong">
                <div className="text-3xl font-display font-bold text-rose-700">{score.wrong}</div>
                <div className="text-xs font-bold uppercase text-rose-700/80">To review</div>
              </div>
              <div className="card-soft !p-4 !border-blue-100 bg-blue-50" data-testid="final-pct">
                <div className="text-3xl font-display font-bold text-[#1D4ED8]">{pct}%</div>
                <div className="text-xs font-bold uppercase text-[#1D4ED8]/80">Score</div>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button onClick={() => startTest(chosenCount)} className="btn-clay-blue" data-testid="retry-btn">
                Play again
              </button>
              {score.wrong > 0 && (
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/wordlists/${id}/missed-words`);
                      const words = data.words || [];
                      setMissedWords(words);
                      if (words.length === 0) { toast.success("No missed words — nothing to review!"); return; }
                      const shuffled = shuffle(words);
                      setChosenCount(shuffled.length);
                      setOrder(shuffled);
                      setIdx(0);
                      setScore({ right: 0, wrong: 0 });
                      setAnswer(""); setFeedback(null);
                    } catch (err) {
                      console.warn("[review-missed] fetch failed:", err);
                      toast.error("Couldn't load missed words");
                    }
                  }}
                  className="btn-clay-yellow"
                  data-testid="review-missed-btn"
                >
                  <RefreshCw className="w-5 h-5" strokeWidth={3} />
                  Review missed words
                </button>
              )}
              <button onClick={() => { setChosenCount(null); setOrder([]); setIdx(0); setScore({ right: 0, wrong: 0 }); }} className="btn-clay-ghost" data-testid="change-count-btn">
                Change count
              </button>
              <button onClick={() => navigate("/dashboard")} className="btn-clay" data-testid="back-home-btn">
                <ArrowRight className="w-5 h-5" strokeWidth={3} /> Home
              </button>
            </div>
          </motion.div>
        );
      })()}
    </div>
  );
}
